import SIGN_UP from "Constants/signupFields";
import PROFILE_FIELD from "Constants/profileFields";
import Analytics from "Services/analytics";
import Config from "config";
import {
  ExternalFormWorkerTypes,
  ExternalFormLeadSource,
} from "Constants/externalFormFields";

import I18nService from "Services/i18n";
import { ApiService } from "Services/api";
import { FetchRequest } from "./fetchRequest";

export const ACCOUNT_TYPE = {
  UNKNOWN: "0",
  INDIVIDUAL: "1",
  COMPANY: "2",
};

export function setExternalData(userData, externalData) {
  if (!externalData) {
    return userData;
  }

  const result = userData;
  result.meta_data.pub_type = externalData.type.value;

  switch (externalData.type.value) {
    case ExternalFormWorkerTypes.WEB_SITE_OWNER:
      result.meta_data.website_url = externalData.websiteUrl;
      break;

    case ExternalFormWorkerTypes.AFFILATE_MEDIA_AGENCY:
      result.meta_data.agency_verticals = externalData.verticals;
      break;

    case ExternalFormWorkerTypes.APP_DEVELOPER:
      result.meta_data.app_name = externalData.appName;
      break;

    case ExternalFormWorkerTypes.OTHER:
      result.meta_data.other = externalData.other;
      break;

    default:
      break;
  }

  result.meta_data.push_using = externalData.pushUsing;

  if (externalData.pushUsing) {
    result.meta_data.provider = externalData.provider;
  }

  result.meta_data.lead_source = ExternalFormLeadSource.MARKETING;

  return result;
}

const clearLocalCounters = () => {
  localStorage.removeItem(USER_COUNT_KEY);
  localStorage.removeItem(SENDING_COUNT_KEY);
  localStorage.removeItem(ACTIVE_USERS_KEY);
};

export function getPreparedUserData(user, language, externalData) {
  const userData = {
    [SIGN_UP.REF_ID]: user[SIGN_UP.REF_ID] ? user[SIGN_UP.REF_ID] : undefined,
    [PROFILE_FIELD.EMAIL]: user[PROFILE_FIELD.EMAIL],
    contact_info: {
      [PROFILE_FIELD.CITY]: user[PROFILE_FIELD.CITY],
      [PROFILE_FIELD.MESSENGER]: user[PROFILE_FIELD.MESSENGER],
      [PROFILE_FIELD.MESSENGER_TYPE]: user[PROFILE_FIELD.MESSENGER_TYPE],
      [PROFILE_FIELD.PHONE_NUMBER]:
        typeof user[PROFILE_FIELD.PHONE_NUMBER] === "string"
          ? user[PROFILE_FIELD.PHONE_NUMBER]?.replace(/\+/g, "")
          : user[PROFILE_FIELD.PHONE_NUMBER],
    },
    meta_data: {
      [PROFILE_FIELD.LANGUAGE]: language,
      [PROFILE_FIELD.IS_AGREE_NEWS_LETTER]:
        user[PROFILE_FIELD.IS_AGREE_NEWS_LETTER] ?? true,
    },
    legal_info: {
      [PROFILE_FIELD.ADDRESS]: user[PROFILE_FIELD.ADDRESS],
      [PROFILE_FIELD.VAT]:
        user[PROFILE_FIELD.ACCOUNT_TYPE] === ACCOUNT_TYPE.COMPANY
          ? user[PROFILE_FIELD.VAT]
          : undefined,
      [PROFILE_FIELD.LAST_NAME]: user[PROFILE_FIELD.LAST_NAME],
      [PROFILE_FIELD.FIRST_NAME]: user[PROFILE_FIELD.FIRST_NAME],
      [PROFILE_FIELD.ACCOUNT_TYPE]: parseInt(
        user[PROFILE_FIELD.ACCOUNT_TYPE],
        10
      ),
      [PROFILE_FIELD.COMPANY_NAME]:
        user[PROFILE_FIELD.ACCOUNT_TYPE] === ACCOUNT_TYPE.COMPANY
          ? user[PROFILE_FIELD.COMPANY_NAME]
          : undefined,
      [PROFILE_FIELD.RESIDENCE_COUNTRY]: user[PROFILE_FIELD.RESIDENCE_COUNTRY],
      [SIGN_UP.AGREEMENT]: user[SIGN_UP.AGREEMENT],
      [PROFILE_FIELD.TERMS_AND_CONDITIONS_OURS_AGREED_AT]:
        user[PROFILE_FIELD.TERMS_AND_CONDITIONS_OURS_AGREED_AT],
    },
    [SIGN_UP.CAPTCHA]: user[SIGN_UP.CAPTCHA] || "default",
  };

  return setExternalData(userData, externalData);
}

function preparedAuthorizeDataToClient(data) {
  const now = new Date();

  return {
    apiToken: data.api_token,
    tokenType: data.type,
    tokenExpiresIn: data.expires_in,
    lastLogin: now.getTime(),
    expirationDate: new Date(now.getTime() + data.expires_in * 1000).getTime(),
    isReadOnly: data.is_read_only !== undefined ? data.is_read_only : false,
    id: data.id,
  };
}

const STORAGE_KEY = "user";
export const USER_COUNT_KEY = "usersCount";
export const SENDING_COUNT_KEY = "sendingCount";
export const ACTIVE_USERS_KEY = "activeUsers";

class User {
  async login(data) {
    // eslint-disable-next-line no-param-reassign
    data.captcha = data.captcha || "default";

    const user = await ApiService.post("/public/login/", data);
    const userData = preparedAuthorizeDataToClient(user);

    await FetchRequest({
      url: `/ssp/token`,
      method: "POST",
      customToken: {
        apiToken: userData.apiToken,
        tokenType: userData.tokenType,
      },
      body: userData
    });

    await this.save(userData);

    clearLocalCounters();

    return userData;
  }

  async create(user, externalData) {
    const defaultLanguage = I18nService.getDefaultLanguage();
    const gaClientId = Analytics.getGoogleAnalyticsClientId();
    const preparedData = getPreparedUserData(
      user,
      defaultLanguage,
      externalData
    );
    preparedData.partner_alias = Config.partnerAlias;
    preparedData.type = Config.role;

    if (gaClientId) {
      preparedData.ga_client_id = gaClientId;
    }

    return ApiService.post("/public/registration/", preparedData).then(
      (result) => {
        Analytics.trackUserId(result.id);
        return result;
      }
    );
  }

  resetVerificationLink(username, recaptcha) {
    const preparedData = {
      username,
      partner_alias: Config.partnerAlias,
      type: Config.role,
      captcha: recaptcha || "default",
    };

    return ApiService.post("/public/reset_token/", preparedData);
  }

  confirmToken(token) {
    const preparedData = {
      token,
    };
    return ApiService.post("/public/confirm/", preparedData);
  }

  async activateAccount(data) {
    const activateData = {
      token: data.token,
      password: {
        confirmation: data.confirmPassword,
        password: data.password,
      },
      fingerprint: data.fingerprint,
    };

    const user = await ApiService.post("/public/set_password/", activateData);
    const userData = preparedAuthorizeDataToClient(user);

    await FetchRequest({
      url: `/ssp/token`,
      method: "POST",
      customToken: {
        apiToken: userData.apiToken,
        tokenType: userData.tokenType,
      },
      body: userData
    });

    await this.save(userData);
    return userData;
  }

  get() {
    const user = JSON.parse(window.localStorage.getItem(STORAGE_KEY));

    return user || undefined;
  }

  save(user) {
    return Promise.resolve().then(() => {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    });
  }

  isAuthenticated() {
    const user = this.get();

    if (user && user.expirationDate) {
      return new Date().valueOf() < user.expirationDate;
    }

    return false;
  }

  isReadOnly() {
    const user = this.get();

    if (user && user.isReadOnly) {
      return user.isReadOnly;
    }

    return false;
  }

  tokenExpiresIn() {
    const user = this.get();

    if (user && user.expirationDate) {
      return Math.floor((user.expirationDate - new Date().valueOf()) / 1000);
    }

    return 0;
  }

  loginAt() {
    const user = this.get();

    if (user && user.lastLogin) {
      return Math.floor(user.lastLogin / 1000);
    }

    return 0;
  }

  async autoLogin(autoLoginData) {
    const userData = preparedAuthorizeDataToClient(autoLoginData);
    await FetchRequest({
      url: `/ssp/token`,
      method: "POST",
      customToken: {
        apiToken: userData.apiToken,
        tokenType: userData.tokenType,
      },
      body: userData
    });

    await this.save(userData);

    clearLocalCounters();

    return userData;
  }

  async clearUserData() {
    clearLocalCounters();
    await this.remove();
  }

  async logout() {
    if (this.get()) {
      await ApiService.get("/logout/");
    }
    clearLocalCounters();
    await this.remove();
  }

  async remove() {
    return window.localStorage.removeItem(STORAGE_KEY);
  }
}

const userInstance = new User();

export default userInstance;